import { isArray, isEmpty } from "lodash-es";
import queryString from "query-string";
//#region src/UrlQueryParam/utils.ts
/**
* Appends query params to the given url.
*
* @param url - The base url.
* @param params - The params to append.
* @return - The url, with query params attached.
*/
function appendQueryParams(url, params) {
	if (!params || isEmpty(params)) return url;
	const query = queryString.stringify(params);
	return query ? `${url}?${query}` : url;
}
/**
* Get decoded query param from url search string, like `?foo=1`
*
* @param search - The query search string (location.search).
* @param propName - The propName within search.
* @param index - Optional index for the query prop, if an array. Default 0
* @return - The matching param, if found.
*/
function getQueryParam(search, propName, options = {}) {
	const { index = 0 } = options;
	const props = queryString.parse(search)[propName];
	if (props == null) return;
	return isArray(props) ? props[index] ?? void 0 : props;
}
/**
* Get decoded query params array from url search string, like `?foo=1&foo=2`
*
* @param search - The query search string (location.search).
* @param propName - The propName within search.
* @return - The matching params array, if found, else empty array
*/
function getQueryParams(search, propName) {
	const props = queryString.parse(search)[propName];
	if (props == null) return [];
	return isArray(props) ? props : [props];
}
var UNSAFE_REDIRECT_LIST = [
	/\/\//,
	/^http/,
	/javascript/,
	/data:/,
	/<.*script:/,
	/[\r\n]/,
	/\\/
];
/**
* Return safe URL, while allowing URLs that are color.com
*/
function getSafeUrl(nextUrl) {
	if (/^http.?:\/\/(home|www|providers)?\.?color.com/.test(nextUrl)) return nextUrl;
	if (UNSAFE_REDIRECT_LIST.some((regex) => regex.test(nextUrl))) return;
	return nextUrl;
}
/**
* Return safe URL from query param matching `next=` search query, while allowing URLs that are color.com
*/
function getSafeNextQueryParam(search) {
	const nextUrl = getQueryParam(search, "next");
	if (!nextUrl) return;
	return getSafeUrl(appendQueryParams(nextUrl, queryString.parse(search.split(nextUrl)[1] ?? "")));
}
/**
* Return safe URL from query param matching `verifyEmailNextUrl=` search query, while allowing URLs that are color.com
*/
function getSafeVerifyEmailNextUrlQueryParam(search) {
	const verifyEmailNextUrl = getQueryParam(search, "verifyEmailNextUrl");
	if (!verifyEmailNextUrl) return;
	return getSafeUrl(appendQueryParams(verifyEmailNextUrl, queryString.parse(search.split(verifyEmailNextUrl)[1] ?? "")));
}
/**
* Simple wrapper to generate a query string from key value pairs.
*
* @param params - key-value pairs. e.g.: { param: 'value' }
* @return - The query search string (with `?` prefix)
*/
function toQueryString(params) {
	return `?${queryString.stringify(params)}`;
}
/**
* Delete query param from url search string. For example, given `?foo=1&bar=2&x=3`, delete `bar`
* returns `?foo=1&x=3`
*
* @param search - The query search string (location.search).
* @param propName - The propName within search to delete.
* @return - The updated query search string (with `?` prefix)
*/
function stripQueryParam(search, propName) {
	const remainder = queryString.exclude(search, [propName]);
	return remainder.length > 0 ? remainder : "?";
}
/**
* Merge multiple query params into a URL search string. Values in `paramsToMerge` overwrite values
* in `search`.
*
* @param search - The query search string (location.search).
* @param updatedParams - Object mapping param names to values.
* @returns - The updated query search string (with `?` prefix)
*/
function mergeQueryParams(search, updatedParams) {
	return toQueryString({
		...queryString.parse(search),
		...updatedParams
	});
}
/**
* merge query param into url search string. Examples:
* given `?foo=1&bar=2&x=3`, merge prop `bar` with value 5
* returns `?bar=5&foo=1&x=3`,
* given `?foo=1&x=3`, merge prop `bar` with value 5
* returns `?bar=5&foo=1&x=3`,
*
* @param search - The query search string (location.search).
* @param propName - The propName to add or update.
* @param propValue - The propValue.
* @return - The updated query search string (with `?` prefix)
*/
function mergeQueryParam(search, propName, propValue) {
	return mergeQueryParams(search, { [propName]: propValue });
}
/**
* Return current pathname plus search.
* Example: /covid/sign-up/start?partner=sfdph
*/
function getCurrentPath() {
	const { pathname, search } = window.location;
	return `${pathname}${search}`;
}
/**
* Return search string with an escaped path to `nextUrl` as the `next` query param.
* `nextUrl` defaults to the current path.
*
* For example:
*   getNextSearchString() when on /covid/sign-up/start?partner=sfdph
*   getNextSearchString('/covid/sign-up/start?partner=sfdph') when on any URL
* both return `?next=%2Fcovid%2Fsign-up%2Fstart%3Fpartner%3Dsfdph`
*/
function getNextSearchString(nextUrl) {
	return mergeQueryParam("", "next", nextUrl ?? getCurrentPath());
}
/**
* Return pathname without a trailing slash, if it has one. `path` should be
* free of any search params.
*/
function trimTrailingSlash(path) {
	return path.replace(/\/$/, "");
}
//#endregion
export { getQueryParams as a, getSafeVerifyEmailNextUrlQueryParam as c, stripQueryParam as d, toQueryString as f, getQueryParam as i, mergeQueryParam as l, getCurrentPath as n, getSafeNextQueryParam as o, trimTrailingSlash as p, getNextSearchString as r, getSafeUrl as s, appendQueryParams as t, mergeQueryParams as u };

//# sourceMappingURL=utils-CT_1OHHh.js.map